/**
 * useActivityRun — le crochet qui branche les effets de l'exécution d'une activité : la file de la
 * copie, le chrono, la finalisation, les relevés, la télémétrie. Sans écran : il rend un état et des
 * gestes, qu'un view-model traduit en props de `<StudentActivityPage>`.
 *
 * Le même crochet sert l'activité hors live (son écran, `activity` / `sam-session`) et l'activité que
 * l'écran du live compose : rien du live n'est lu ici. Ce que le live apporte arrive par `live`
 * (`ActivityLiveInputs`) ; hors live, tout y est absent, et chaque entrée est un no-op.
 *
 * L'ancien code (`ActivityScreen.js`, `TimerActivityStep.js`, `AudioVideoActivityStep.js`) en est la
 * vérité des données et des gestes ; ce qui change est dit à chaque bloc.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../data/services/activitiesService"
import { skillSetsService } from "../../data/services/skillSetsService"
import { incrementActivitiesCount } from "../../data/utility/asyncStorageUtils"
import { ownNetworkFailures } from "../../data/utility/networkFailureOwner"
import { useOutbox } from "../shared/outbox/outbox"
import { apiCall, ApiCallError, apiFailureOf, type ApiService } from "../shared/apiCall"
import { createTelemetry, httpStatusOf, type EngineTelemetry } from "../shared/telemetry"
import { activityRunReducer, initialRunState, shouldFinalize, type RunEvent, type RunState } from "./activityRunMachine"
import { skillSetsToMarkRead, type ApiFinalizeResult } from "./celebration/activityCongrats"
import {
    acquireChrono,
    chronoDisplay,
    chronoSeconds,
    type ChronoDisplay,
    type ChronoHandle,
    type ChronoState,
} from "./chrono"
import {
    copyOutbox,
    tmpKeyOf,
    type ApiCopyLog,
    type CopyFile,
    type CopyKind,
    type CopyOutbox,
    type CopyTarget,
} from "./copy/copyOutbox"
import {
    copyPieces,
    copyReadiness,
    outboxItemsOfPiece,
    pendingAttachments,
    serverCopyLogs,
    type CopyPiece,
    type ServerCopyLog,
} from "./copy/copyPieces"
import { classifyFinalizeError } from "./finalizeOutcome"
import { workMediumOf } from "./workMedium"

/**
 * Ce que le live apporte à l'activité qu'il compose. Hors live : rien (chaque entrée absente est un
 * no-op). L'écran du live les branche sur sa salle et son état :
 *  - `liveSessionId` : la séance, que `updateChrono` et la suppression d'une pièce reçoivent
 *    (`live_mode`, `live_session_id`), comme aujourd'hui ;
 *  - `paused` : la pause du tuteur (`activity.activityPaused`, le Concentré en groupe, un quiz en
 *    cours) — le chrono s'arrête, l'enregistreur se met en pause ;
 *  - `announceWorkUpdated` : une page de la copie arrivée ou retirée (l'événement de salle
 *    `studentWorkUpdated`, `{ studentId, activityId, mediaType: "photo", count }`) ;
 *  - `announceFinalized` : la copie rendue (`activityFinalized`, aux tuteurs de la séance) ;
 *  - `setVoiceRecording` : l'attribut `voice_recording_activity` (`setVoiceRecordingActivityV2`) —
 *    vrai à l'ouverture de l'enregistreur, FAUX à toute sortie (fermeture, envoi, rendu, autre
 *    activité, démontage) : l'ancien ne le remettait jamais à faux ;
 *  - `removed` : le tuteur a archivé l'activité ouverte (`deleteTodo`) — elle n'existe plus, l'écran
 *    le dit (« Cette activité n'est plus disponible »), puis passe à la suivante ;
 *  - `finalizedByTutor` : le tuteur l'a rendue à la place de l'élève (`tutorFinalizedActivity`) — elle
 *    passe « validée » et se relit ;
 *  - `openReplayed` : « Refaire l'activité » sur une activité finie, dans le volet du live — le live
 *    ouvre la nouvelle et prévient le tuteur ;
 *  - `correctionLeft` : la correction qui n'est pas attendue à l'écran (l'attente quittée avant elle,
 *    ou sautée en Dialogue) — le live la surveille et dit à l'élève, où qu'il soit, qu'elle est prête
 *    (`useCorrectionWatch`) ;
 *  - `dialogue` : le masteur est là (Wajih, 25/10) — la copie soumise ne fait pas attendre sa
 *    correction, et l'élève reste sur l'activité rendue ; la correction s'annonce à son arrivée.
 * L'activité que le tuteur pousse arrive, elle, par `activity` : une autre activité remet tout à zéro
 * (et l'ancienne, si elle a une copie, est finalisée en silence, comme `useFinalizeSilentlyIfHasMedia`).
 */
export type ActivityLiveInputs = {
    liveSessionId?: number | null
    paused?: boolean
    announceWorkUpdated?: (info: { activityId: number; mediaType: "photo"; count: number }) => void
    announceFinalized?: (activity: { id: number }) => void
    setVoiceRecording?: (enabled: boolean) => void
    removed?: boolean
    finalizedByTutor?: boolean
    openReplayed?: (activity: { id?: number | null } & Record<string, unknown>) => void
    correctionLeft?: (correction: { id: number; title: string }) => void
    dialogue?: boolean
}

/** L'activité telle que le serveur la sert (la todo, l'activité courante). */
export type RunActivity = {
    id?: number | null
    duration?: number | null
    estimated_duration?: number | null
    activity_template_id?: number | null
    activityTemplateId?: number | null
    activity_logs?: ApiCopyLog[] | null
} & Record<string, unknown>

export type RunTemplateStep = { type?: string | null; description_init?: string | null }

export type UseActivityRunOptions = {
    activity: RunActivity | null
    /** Les étapes du modèle de l'activité (`staticData.activity_templates[].steps`). */
    templateSteps: RunTemplateStep[] | null | undefined
    live?: ActivityLiveInputs
    /** La file de la copie (celle de l'app par défaut ; un test passe la sienne). */
    outbox?: CopyOutbox
}

export type ActivityRunNotice = "delete_failed" | null

export type ActivityRun = {
    activityId: number | null
    medium: CopyKind
    state: RunState
    pieces: CopyPiece[]
    /** La copie part encore (« J'ai fini » tourne en l'attendant). */
    copyInFlight: boolean
    /** Le chrono, à l'instant de son dernier changement : `<ActivityChrono>` décompte seul ensuite. */
    chrono: ChronoDisplay
    /** Les consignes des deux premières étapes du modèle (l'énoncé sans support, la copie). */
    stepDescriptions: [string | null, string | null]
    /**
     * Le support de l'activité tel que le serveur le sert à son ouverture (la relecture), ou `null`
     * tant qu'il n'a pas répondu : l'énoncé se lit de là d'abord. L'activité reçue a pu partir d'une
     * liste chargée avant que son énoncé arrive (un énoncé tapé, généré ou lu sur photo après coup).
     */
    material: Record<string, unknown> | null
    notice: ActivityRunNotice
    /**
     * Où part une pièce prise maintenant : l'activité ouverte et son étape de copie ; `null` sans
     * activité. L'écran le fige au geste qui ouvre le scanner ou la caméra.
     */
    copyTarget: () => CopyTarget | null
    /**
     * Une pièce dans la file : vers `target` (figé au geste), à défaut vers l'activité ouverte. Rend son
     * id dans la file ; `null` quand elle n'a nulle part où aller (rien n'est mis en file).
     */
    capture: (file: CopyFile, target?: CopyTarget | null) => string | null
    retryPiece: (outboxId: string) => void
    deletePiece: (key: string) => void
    finish: () => void
    dismissFailure: () => void
    requestBack: () => void
    cancelBack: () => void
    confirmExit: () => void
    correctionDone: () => void
    /** « Revenir à l'activité » : la copie soumise en Dialogue, l'activité rendue se relit. */
    backToActivity: () => void
    clearNotice: () => void
    /** L'enregistreur s'ouvre (activité orale) : l'attribut vocal du live passe à vrai. */
    recorderOpened: () => void
    /** L'enregistreur se ferme, quelle qu'en soit la raison : l'attribut vocal repasse à faux. */
    recorderClosed: () => void
    /** « Réenregistrer » : l'enregistrement d'avant est retiré (serveur et file). */
    discardRecording: () => void
}

type Dispatch = (action: never) => unknown

const NO_LIVE: ActivityLiveInputs = {}
/**
 * Le temps minimum d'un « J'ai fini » dont la copie échoue. Un stockage injoignable échoue en quelques
 * dizaines de millisecondes : la boîte disparaissait 77 ms et revenait à l'identique, et « Réessayer »
 * semblait ne rien faire.
 */
const COPY_FAILURE_MIN_MS = 1200
const NO_HIDDEN: ReadonlySet<string> = new Set()

/** L'étape de la copie, à partir de 1 (`ActivityScreen.mediaStepIndex` : 2 à défaut, l'ancienne forme). */
function mediaStepOf(steps: RunTemplateStep[] | null | undefined): number {
    const index = (steps ?? []).findIndex((step) => step?.type === "photo" || step?.type === "audio_video")
    return index >= 0 ? index + 1 : 2
}

/**
 * Le lecteur du chrono de l'activité ouverte, et d'aucune autre : le rendu qui change d'activité tient
 * encore celui de l'ancienne (pris par un effet, il arrive au rendu suivant) ; ses effets la piloteraient.
 */
function useChrono(activityId: number | null, initialSeconds: number): ChronoHandle | null {
    const [held, setHeld] = useState<{ activityId: number; handle: ChronoHandle } | null>(null)
    const initialRef = useRef(initialSeconds)
    initialRef.current = initialSeconds
    useEffect(() => {
        if (activityId == null) return
        const handle = acquireChrono(activityId, initialRef.current)
        setHeld({ activityId, handle })
        return () => {
            handle.release()
            setHeld(null)
        }
    }, [activityId])
    return held != null && held.activityId === activityId ? held.handle : null
}

const STOPPED_CHRONO: ChronoState = { baseMs: 0, runningSince: null, paused: false, stopped: true }
const noopSubscribe = () => () => {}

export function useActivityRun(options: UseActivityRunOptions): ActivityRun {
    const { activity, templateSteps } = options
    const live = options.live ?? NO_LIVE
    const outbox = options.outbox ?? copyOutbox()
    const dispatch = useDispatch() as unknown as Dispatch
    const activityId =
        typeof activity?.id === "number" ? activity.id : activity?.id != null ? Number(activity.id) : null

    const telemetryRef = useRef<EngineTelemetry | null>(null)
    telemetryRef.current ??= createTelemetry("activity", { role: "student" })
    const telemetry = telemetryRef.current

    // Les entrées du live, lues au moment de l'effet : elles changent d'identité à chaque rendu.
    const liveRef = useRef(live)
    liveRef.current = live
    const liveParams = useCallback(() => {
        const sessionId = liveRef.current.liveSessionId ?? null
        return { live_mode: sessionId != null, live_session_id: sessionId ?? undefined }
    }, [])

    /* ── la machine ── */
    const [state, dispatchRun] = useReducer(activityRunReducer, activityId, initialRunState)
    const stateRef = useRef(state)
    stateRef.current = state
    // Un fil d'Ariane par transition : l'état d'arrivée se calcule ici (le réducteur reste pur, et un
    // double appel du réducteur ne double pas le fil).
    const send = useCallback(
        (event: RunEvent) => {
            const current = stateRef.current
            const next = activityRunReducer(current, event)
            if (next !== current && (next.phase !== current.phase || next.failure !== current.failure)) {
                telemetry.transition(
                    `${current.phase}${current.failure ? `/${current.failure}` : ""}`,
                    `${next.phase}${next.failure ? `/${next.failure}` : ""}`,
                    event.type
                )
            }
            stateRef.current = next
            dispatchRun(event)
        },
        [telemetry]
    )
    // L'écran dit lui-même ses échecs réseau (la copie, la finalisation) : les boîtes legacy se taisent.
    useEffect(() => ownNetworkFailures(), [])
    useEffect(() => {
        send({ type: "OPENED", activityId })
    }, [activityId, send])

    const medium = workMediumOf(templateSteps)
    const mediaStep = mediaStepOf(templateSteps)

    /* ── la copie : ce que le serveur a, et la file ── */
    const [serverLogs, setServerLogs] = useState<ServerCopyLog[]>(() => serverCopyLogs(activity?.activity_logs))
    const [hidden, setHidden] = useState<ReadonlySet<string>>(NO_HIDDEN)
    const [notice, setNotice] = useState<ActivityRunNotice>(null)
    const [freshMaterial, setFreshMaterial] = useState<{
        activityId: number
        material: Record<string, unknown>
    } | null>(null)
    const outboxState = useOutbox(outbox)
    const pieces = useMemo(
        () => copyPieces(activityId, serverLogs, outboxState.items, hidden),
        [activityId, serverLogs, outboxState.items, hidden]
    )
    const readiness = copyReadiness(pieces)

    // Le temps que le serveur a reçu (`update-chrono`), le plus grand vu : le chrono, pris peut-être
    // depuis un compte gardé en mémoire (un nouvel onglet, un rechargement), le rattrape.
    const chronoRef = useRef<ChronoHandle | null>(null)
    const serverSecondsRef = useRef(0)
    const seeServerSeconds = useCallback((seconds: number | null | undefined) => {
        if (typeof seconds !== "number" || !(seconds > 0)) return
        serverSecondsRef.current = Math.max(serverSecondsRef.current, seconds)
        chronoRef.current?.dispatch({ type: "SERVER_SEEN", seconds: serverSecondsRef.current, now: Date.now() })
    }, [])

    /** La relecture des journaux de l'activité (`ActivityScreen.refresh`) : la copie que le serveur a. */
    const openedIdRef = useRef(activityId)
    openedIdRef.current = activityId
    const refresh = useCallback(() => {
        if (activityId == null) return
        apiCall<RunActivity[]>(dispatch, activitiesService.getActivities as ApiService, {
            urlParams: { with_logs: 1, activity_ids: activityId },
        })
            .then((rows) => {
                // Partie avant un changement d'activité : sa copie et son temps sont ceux de l'ancienne.
                if (openedIdRef.current !== activityId) return
                const fresh = (rows ?? []).find((row) => row?.id === activityId)
                if (fresh != null) setServerLogs(serverCopyLogs(fresh.activity_logs))
                // Comme l'ancien (le réducteur de `getActivities` remplaçait l'activité courante) :
                // l'énoncé arrivé après la liste se lit dès l'ouverture.
                const material = fresh?.material
                if (material != null && typeof material === "object") {
                    setFreshMaterial({ activityId, material: material as Record<string, unknown> })
                }
                seeServerSeconds(fresh?.duration)
            })
            .catch((error) => {
                // La copie à l'écran garde ce qu'elle a ; la prochaine relecture rattrapera.
                telemetry.error("refresh", error)
            })
    }, [activityId, dispatch, telemetry, seeServerSeconds])

    // À l'ouverture d'une activité : sa copie d'avant (un oral fait plus tôt, des pages déjà parties).
    useEffect(() => {
        setServerLogs(serverCopyLogs(activity?.activity_logs))
        setHidden(NO_HIDDEN)
        setNotice(null)
        refresh()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId])

    // Une pièce arrivée : la copie est relue (le tuteur en séance la voit à l'annonce).
    const sentCount = outboxState.items.filter(
        (item) => item.target.activityId === activityId && item.status === "sent"
    ).length
    const lastSentRef = useRef(sentCount)
    useEffect(() => {
        if (sentCount > lastSentRef.current && activityId != null) {
            refresh()
            if (medium === "photo") {
                liveRef.current.announceWorkUpdated?.({ activityId, mediaType: "photo", count: pieces.length })
            }
        }
        lastSentRef.current = sentCount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sentCount])

    const copyTarget = useCallback(
        (): CopyTarget | null =>
            activityId == null
                ? null
                : { activityId, kind: medium, mediaStep, previousStep: Math.max(mediaStep - 1, 1) },
        [activityId, medium, mediaStep]
    )
    const capture = useCallback(
        (file: CopyFile, target?: CopyTarget | null) => {
            const destination = target ?? copyTarget()
            return destination != null ? outbox.enqueue(file, destination) : null
        },
        [copyTarget, outbox]
    )

    const retryPiece = useCallback((outboxId: string) => outbox.retry(outboxId), [outbox])

    /**
     * Supprimer une pièce : une pièce de la file qui n'est pas partie la quitte ; une pièce enregistrée
     * voit son journal retiré (`DELETE api/activity-logs/{id}`), et sa clé S3 avec elle — sinon la
     * finalisation la recollerait. L'ancien supprimait l'oral vers `currentAudio?.id`, souvent absent.
     */
    const deletePiece = useCallback(
        (key: string) => {
            const piece = pieces.find((candidate) => candidate.key === key)
            if (piece == null || activityId == null) return
            const dropFromOutbox = (logId: number | null) => {
                outboxItemsOfPiece(piece, logId, outboxState.items).forEach((item) => outbox.remove(item.id))
            }
            if (piece.logId == null) {
                if (piece.status !== "sending") dropFromOutbox(null)
                return
            }
            const logId = piece.logId
            setHidden((current) => new Set([...current, key]))
            apiCall(dispatch, activitiesService.deleteActivityLog as ApiService, {
                routeParams: { id: logId },
                data: liveParams(),
            })
                .then(() => {
                    dropFromOutbox(logId)
                    setServerLogs((logs) => logs.filter((log) => log.id !== logId))
                    if (medium === "photo") {
                        liveRef.current.announceWorkUpdated?.({
                            activityId,
                            mediaType: "photo",
                            count: Math.max(0, pieces.length - 1),
                        })
                    }
                })
                .catch((error) => {
                    telemetry.error("delete_piece", error)
                    if (error instanceof ApiCallError && error.status === 404) {
                        // Le journal n'existe plus : la pièce est déjà partie du serveur.
                        dropFromOutbox(logId)
                        setServerLogs((logs) => logs.filter((log) => log.id !== logId))
                        return
                    }
                    setNotice("delete_failed")
                })
                .finally(() =>
                    setHidden((current) => {
                        const next = new Set(current)
                        next.delete(key)
                        return next
                    })
                )
        },
        [activityId, dispatch, liveParams, medium, outbox, outboxState.items, pieces, telemetry]
    )

    /* ── le chrono ── */
    const chrono = useChrono(activityId, activity?.duration ?? 0)
    chronoRef.current = chrono
    useEffect(() => {
        serverSecondsRef.current = 0
    }, [activityId])
    useEffect(() => {
        // Le chrono vient d'être pris : ce que le serveur a déjà dit (la relecture est peut-être
        // arrivée avant lui), puis chaque `duration` relu avec le compte.
        seeServerSeconds(activity?.duration)
        if (chrono != null && serverSecondsRef.current > 0) {
            chrono.dispatch({ type: "SERVER_SEEN", seconds: serverSecondsRef.current, now: Date.now() })
        }
    }, [chrono, activity?.duration, seeServerSeconds])
    const chronoState = useSyncExternalStore(
        chrono?.subscribe ?? noopSubscribe,
        () => chrono?.getState() ?? STOPPED_CHRONO,
        () => chrono?.getState() ?? STOPPED_CHRONO
    )
    useEffect(() => {
        if (chrono == null || activityId == null) return
        // Un seul relevé toutes les 30 s pour cette activité, même lue par deux écrans : le magasin le tient.
        chrono.setReporter((seconds) => {
            activitiesService
                .updateChrono({ id: activityId }, { chrono_duration: seconds, ...liveParams() })
                .catch((error: unknown) => {
                    const status = httpStatusOf(error)
                    // Sans réponse (hors ligne) : le relevé suivant renvoie le temps, un fil d'Ariane
                    // suffit — une erreur toutes les 30 s par élève coupé noierait Bugsnag.
                    if (status == null) telemetry.transition("chrono", "unsent", "offline")
                    else telemetry.error("update_chrono", error)
                    if (status === 404) send({ type: "ACTIVITY_GONE" })
                })
        })
    }, [activityId, chrono, liveParams, telemetry, send])
    const paused = !!live.paused
    useEffect(() => {
        chrono?.dispatch({ type: paused ? "PAUSE" : "RESUME", now: Date.now() })
    }, [chrono, paused])
    const estimatedSeconds = (activity?.estimated_duration ?? 0) * 60
    const chronoView = useMemo(
        () => chronoDisplay(chronoState, estimatedSeconds, Date.now()),
        [chronoState, estimatedSeconds]
    )

    /* ── « J'ai fini » et la finalisation ── */
    // Les activités rendues par ce crochet (au geste, ou en silence au départ) : aucune ne repart.
    const finalizedRef = useRef(new Set<number>())
    const finish = useCallback(() => {
        // Comme l'ancien `onSubmit` : la copie qui n'est pas partie repart au même geste.
        readiness.failedIds.forEach((id) => outbox.retry(id))
        send({ type: "FINISH_REQUESTED", stays: liveRef.current.dialogue === true })
    }, [outbox, readiness.failedIds, send])

    // Une pièce encore en échec une fois la copie arrivée : l'élève le voit.
    const copySettled = !readiness.inFlight && readiness.failedIds.length === 0
    const finishingSinceRef = useRef(0)
    useEffect(() => {
        if (state.phase === "finishing") finishingSinceRef.current = Date.now()
    }, [state.phase])
    useEffect(() => {
        if (state.phase !== "finishing" || state.finalizing || readiness.inFlight || readiness.failedIds.length === 0) {
            return
        }
        const timer = setTimeout(
            () => send({ type: "COPY_FAILED" }),
            Math.max(0, COPY_FAILURE_MIN_MS - (Date.now() - finishingSinceRef.current))
        )
        return () => clearTimeout(timer)
    }, [state.phase, state.finalizing, readiness.inFlight, readiness.failedIds.length, send])

    const keys = pendingAttachments(activityId, outboxState.items, hidden)
    const keysRef = useRef(keys)
    keysRef.current = keys
    const stopped = state.phase !== "working"
    useEffect(() => {
        if (stopped) chrono?.dispatch({ type: "STOP", now: Date.now() })
    }, [chrono, stopped])

    useEffect(() => {
        if (!shouldFinalize(state, copySettled) || activityId == null) return
        send({ type: "FINALIZE_STARTED" })
        const pending = keysRef.current
        const body: { chrono_duration: number; pending_attachments?: typeof pending } = {
            chrono_duration: chronoSeconds(chrono?.getState() ?? STOPPED_CHRONO, Date.now()),
        }
        if (pending.length > 0) body.pending_attachments = pending
        apiCall<ApiFinalizeResult>(dispatch, activitiesService.finalizeActivity as ApiService, {
            routeParams: { id: activityId },
            data: body,
        })
            .then((result) => {
                finalizedRef.current.add(activityId)
                outboxState.items
                    .filter((item) => item.target.activityId === activityId)
                    .forEach((item) => outbox.remove(item.id))
                liveRef.current.setVoiceRecording?.(false)
                liveRef.current.announceFinalized?.({ id: activityId })
                incrementActivitiesCount().catch((error: unknown) => telemetry.error("activities_count", error))
                send({ type: "FINALIZE_SUCCEEDED", result: result ?? {} })
            })
            .catch((error) => {
                const failure = apiFailureOf(error)
                const kind = classifyFinalizeError(failure)
                if (kind === "already_submitted") {
                    finalizedRef.current.add(activityId)
                    outboxState.items
                        .filter((item) => item.target.activityId === activityId)
                        .forEach((item) => outbox.remove(item.id))
                    liveRef.current.setVoiceRecording?.(false)
                    refresh()
                } else {
                    telemetry.error("finalize", error, { kind, pendingKeys: pending.length })
                }
                send({ type: "FINALIZE_FAILED", kind, canResend: pending.length > 0 })
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.phase, state.finalizing, state.failure, state.notSavedResent, copySettled, activityId])

    /* ── l'écran de fin : l'évolution des quêtes marquée lue, une fois par activité rendue ── */
    // L'ancien écran de fin l'appelait à son montage (`ActivityFinalizedCongrats`) ; sans quête, rien
    // ne part (l'appel revenait en 500 avec `skill_set_ids` vide).
    const markedReadRef = useRef<number | null>(null)
    useEffect(() => {
        if (state.phase !== "congrats" || activityId == null || markedReadRef.current === activityId) return
        markedReadRef.current = activityId
        const ids = skillSetsToMarkRead(state.result)
        if (ids.length === 0) return
        apiCall(dispatch, skillSetsService.markSkillSetEvolutionAsRead as ApiService, {
            data: { skill_set_ids: ids },
        }).catch((error: unknown) => telemetry.error("mark_skill_sets_read", error))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.phase, activityId])

    /* ── l'ancienne activité, remplacée par une autre : rendue en silence si elle a une copie ── */
    // Comme l'ancien `useFinalizeSilentlyIfHasMedia` : quitter une activité dont le serveur tient déjà
    // une page rend cette activité, une fois. L'activité quittée s'oublie au changement, même vers
    // « rien » (une activité faite que le tuteur affiche) : sinon le changement suivant la rendait une
    // seconde fois. Et une activité déjà rendue (ou en train de l'être) ne repart pas.
    const previousRef = useRef<{ id: number; hadCopy: boolean; chrono: ChronoHandle | null; phase: string } | null>(
        null
    )
    useEffect(() => {
        const previous = previousRef.current
        if (previous == null || previous.id === activityId) return
        previousRef.current = null
        if (previous.hadCopy && previous.phase === "working" && !finalizedRef.current.has(previous.id)) {
            finalizedRef.current.add(previous.id)
            const items = outbox.getState().items
            const pending = pendingAttachments(previous.id, items)
            // Les pièces que la finalisation emporte (leur clé S3) : seules elles quittent la file une fois
            // l'activité rendue. Une page restée en échec sans clé n'est pas partie : elle reste, et se
            // renvoie (avant, elle était jetée en silence).
            const carried = new Set(
                items
                    .filter((item) => item.target.activityId === previous.id && tmpKeyOf(item) != null)
                    .map((item) => item.id)
            )
            const body: Record<string, unknown> = {}
            // Son lecteur vient d'être rendu (au même changement) : son temps est figé à l'instant où
            // l'activité est quittée.
            if (previous.chrono != null) body.chrono_duration = chronoSeconds(previous.chrono.getState(), Date.now())
            if (pending.length > 0) body.pending_attachments = pending
            apiCall(dispatch, activitiesService.finalizeActivitySilent as ApiService, {
                routeParams: { id: previous.id },
                data: body,
            })
                .then(() => {
                    liveRef.current.announceFinalized?.({ id: previous.id })
                    carried.forEach((id) => outbox.remove(id))
                })
                .catch((error) => {
                    const kind = classifyFinalizeError(apiFailureOf(error))
                    if (kind === "already_submitted") return
                    // Pas rendue : le prochain départ de cette activité la rendra.
                    finalizedRef.current.delete(previous.id)
                    telemetry.error("finalize_silent", error, { kind })
                })
            liveRef.current.setVoiceRecording?.(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId])
    useEffect(() => {
        if (activityId == null) return
        previousRef.current = { id: activityId, hadCopy: serverLogs.length > 0, chrono, phase: state.phase }
    }, [activityId, serverLogs.length, chrono, state.phase])

    /* ── les gestes du tuteur sur l'activité ouverte (en live) ── */
    const removed = !!live.removed
    const finalizedByTutor = !!live.finalizedByTutor
    useEffect(() => {
        if (removed) send({ type: "ACTIVITY_GONE" })
    }, [removed, activityId, send])
    useEffect(() => {
        if (!finalizedByTutor || activityId == null) return
        // Rendue par le tuteur : rien ne repart d'ici, ni la copie, ni l'enregistreur.
        finalizedRef.current.add(activityId)
        liveRef.current.setVoiceRecording?.(false)
        send({ type: "TUTOR_FINALIZED" })
    }, [finalizedByTutor, activityId, send])

    /* ── l'enregistreur : l'attribut vocal du live remis à faux à toute sortie ── */
    const recorderOpenRef = useRef(false)
    const recorderOpened = useCallback(() => {
        recorderOpenRef.current = true
        liveRef.current.setVoiceRecording?.(true)
    }, [])
    const recorderClosed = useCallback(() => {
        if (!recorderOpenRef.current) return
        recorderOpenRef.current = false
        liveRef.current.setVoiceRecording?.(false)
    }, [])
    useEffect(
        () => () => {
            if (recorderOpenRef.current) liveRef.current.setVoiceRecording?.(false)
            recorderOpenRef.current = false
        },
        [activityId]
    )

    const discardRecording = useCallback(() => {
        pieces.filter((piece) => piece.status !== "sending").forEach((piece) => deletePiece(piece.key))
    }, [deletePiece, pieces])

    const firstDescription = templateSteps?.[0]?.description_init ?? null
    const secondDescription = templateSteps?.[1]?.description_init ?? null

    return {
        activityId,
        medium,
        state,
        pieces,
        copyInFlight: readiness.inFlight,
        chrono: chronoView,
        stepDescriptions: [firstDescription, secondDescription],
        material: freshMaterial != null && freshMaterial.activityId === activityId ? freshMaterial.material : null,
        notice,
        copyTarget,
        capture,
        retryPiece,
        deletePiece,
        finish,
        dismissFailure: useCallback(() => send({ type: "FAILURE_DISMISSED" }), [send]),
        requestBack: useCallback(
            () => send({ type: "BACK_REQUESTED", hasWork: readiness.hasWork }),
            [readiness.hasWork, send]
        ),
        cancelBack: useCallback(() => send({ type: "BACK_CANCELLED" }), [send]),
        // « Soumettre et quitter » rend la copie comme « J'ai fini » : ce qui n'est pas parti repart.
        confirmExit: useCallback(() => {
            if (stateRef.current.exitQuestion === "with_work") readiness.failedIds.forEach((id) => outbox.retry(id))
            send({ type: "EXIT_CONFIRMED" })
        }, [outbox, readiness.failedIds, send]),
        correctionDone: useCallback(() => send({ type: "CORRECTION_DONE" }), [send]),
        backToActivity: useCallback(() => send({ type: "BACK_TO_ACTIVITY" }), [send]),
        clearNotice: useCallback(() => setNotice(null), []),
        recorderOpened,
        recorderClosed,
        discardRecording,
    }
}
